/**
 * PREVIEW: up to `MAX_COMPANIONS` saved projects loaded READ-ONLY beside the open one, playing the
 * same pose ("companions").
 *
 * The point is comparison — does this walk cycle read the same on the heavy character as on the
 * light one — so a companion is deliberately *not* a second open project. It gets no mapping
 * draft, no placement, no skin params, no mode change, no autosave and no undo entries; nothing
 * here writes to `session.model` / `session.rig`, and `saveProjectState` never sees a companion.
 * The only state a companion produces is its name in `EditorState.companions`, for the panel.
 *
 * Loading reuses exactly what `openProject` does with the stored bytes (blob → `importGlbFromArrayBuffer`
 * → binding or assembly), but stops before every side effect that says "this is what you are
 * editing now". A project that is not finished — a rigged model with no confirmed mapping, an
 * unrigged one with no skin weights — cannot be previewed, because there is no rig to pose.
 */

import { AppError, RIG_VERSION } from '../../shared';
import type { SkinData } from '../../shared';
import type { Object3D } from 'three';

import { importGlbFromArrayBuffer } from '../../importExport';
import { blobsRepo, projectsRepo } from '../../persistence';
import { buildBinding } from '../../rig/mappingValidation';
import { assembleSkinnedRig } from '../../rig/assembleRig';
import { analyzeSkeleton } from '../../rig/sourceSkeleton';
import { bindRig } from '../../runtime';
import type { BoundRig } from '../../runtime';
import type { SessionCompanion } from '../session';
import { disposeSubtree } from '../viewport/disposeResources';
import { MAX_COMPANIONS } from '../types';
import type { CompanionMeta } from '../types';
import type { ControllerDeps } from './internal';
import { guard } from './internal';
import type { PreviewController } from './types';

/**
 * The preview controller as `createControllers` sees it: the pinned contract plus the teardown of
 * its own store/event subscription. Internal to this directory, like `InternalRigController`.
 */
export interface InternalPreviewController extends PreviewController {
  dispose(): void;
}

/**
 * Release the GPU-side resources of one companion.
 *
 * Safe unconditionally: every companion comes out of its own `importGlbFromArrayBuffer` call, and
 * GLTFLoader parses fresh geometries, materials and textures per parse — nothing under a
 * companion root is shared with the open model or with another companion, even when two
 * companions are the same project loaded twice (they cannot be, but the reasoning is what makes
 * the disposal safe rather than the guard).
 *
 * The body used to live here and was, until 2026-08-22, the only correct disposal in the editor.
 * It now delegates to `viewport/disposeResources`, which `session.setModel` / `session.clear()`
 * use for the main model too (audit §2.6 #1) — one traversal, so the two paths cannot drift.
 *
 * The old comment here also justified the main model having no disposal at all, on the grounds
 * that "the previous meshes are still referenced by undo snapshots and the rig". That was wrong
 * and is worth recording, because it is what kept the leak open: undo entries are
 * `{label, key, undo, redo}` closures over clip data (`src/editor/actions/context.ts`), and
 * neither `src/editor/actions/` nor `src/editor/undo.ts` imports three at all — no undo entry has
 * ever held a mesh. What IS shared is narrower and lives one level down: the skinned clone and
 * the source mesh share a material object, which is why `session.setModel` keeps the import
 * owned across a re-skin instead of freeing it.
 */
function disposeCompanion(companion: SessionCompanion): void {
  disposeSubtree(companion.root);
}

export function createPreviewController(deps: ControllerDeps): InternalPreviewController {
  const { ctx } = deps;
  const { store, session, events } = ctx;

  /** One load at a time: re-entry would race two `session.addCompanion` calls past the limit. */
  let busy = false;

  /** Which project the companions belong to, so a project switch can drop them. */
  let ownerProjectId: string | null = store.getState().project?.id ?? null;

  /** Names + kinds, kept beside the session list because `SessionCompanion` carries neither. */
  const meta = new Map<string, CompanionMeta>();

  /**
   * What this controller is responsible for disposing, in load order.
   *
   * Deliberately NOT derived from `session.companions`: `session.clear()` (project close) empties
   * that list in place, and if this were the only record of what was loaded, the GPU resources of
   * every companion would leak on exactly the transition that is supposed to release them.
   */
  const owned = new Map<string, SessionCompanion>();

  function publish(): void {
    // Read the session list, not `owned`: it is what the viewport renders, and a `session.clear()`
    // that already emptied it must publish an empty list rather than a stale one.
    store.actions.setCompanions(
      session.companions.map(
        (companion) =>
          meta.get(companion.projectId) ?? {
            projectId: companion.projectId,
            name: companion.projectId,
            kind: 'rigged',
          },
      ),
    );
    events.emit('companions-changed');
  }

  function dropAll(): void {
    session.clearCompanions();
    for (const companion of owned.values()) disposeCompanion(companion);
    owned.clear();
    meta.clear();
  }

  const controller: InternalPreviewController = {
    async addCompanion(projectId: string): Promise<void> {
      return guard(ctx, 'The preview model could not be loaded.', async () => {
        if (busy) {
          throw new AppError(
            'COMPANION_BUSY',
            'A preview model is still loading — try again in a moment.',
          );
        }
        const openId = store.getState().project?.id ?? null;
        if (projectId === openId) {
          throw new AppError(
            'COMPANION_IS_OPEN_PROJECT',
            'That project is the one you are editing; a preview model has to be a different one.',
            { projectId },
          );
        }
        if (session.companions.some((c) => c.projectId === projectId)) {
          throw new AppError(
            'COMPANION_DUPLICATE',
            'That project is already loaded as a preview model.',
            { projectId },
          );
        }
        if (session.companions.length >= MAX_COMPANIONS) {
          throw new AppError(
            'COMPANION_LIMIT',
            `Up to ${MAX_COMPANIONS} preview models at a time — remove one first.`,
            { limit: MAX_COMPANIONS },
          );
        }

        busy = true;
        try {
          const record = await projectsRepo.get(projectId);
          if (!record) {
            throw new AppError('PROJECT_NOT_FOUND', `No project with id "${projectId}".`, {
              projectId,
            });
          }
          const blob = await blobsRepo.get(record.sourceGlbBlobId);
          if (!blob) {
            throw new AppError(
              'PROJECT_BLOB_MISSING',
              `The stored GLB for project "${record.name}" is gone; re-import the model.`,
              { projectId, blobId: record.sourceGlbBlobId },
            );
          }

          const model = await importGlbFromArrayBuffer(await blob.arrayBuffer());

          let root: Object3D;
          let rig: BoundRig;

          if (record.kind === 'rigged') {
            const saved = record.binding;
            if (!saved) {
              throw new AppError(
                'COMPANION_UNMAPPED',
                `"${record.name}" has no confirmed mapping yet — open it and finish mapping ` +
                  'before using it as a preview model.',
                { projectId },
              );
            }
            // Same version guard as `openProject`, minus its toast: a companion is read-only, so
            // "clips authored before the upgrade may sit differently" is not advice about work
            // the user is doing here.
            const binding =
              saved.rigVersion !== RIG_VERSION
                ? buildBinding(
                    saved.canonicalToSourceBone,
                    analyzeSkeleton(model.root).bones,
                    saved.worldCorrection,
                  )
                : saved;
            root = model.root;
            rig = bindRig(model.root, binding);
          } else {
            const skinData: SkinData[] | undefined = record.skinData;
            if (!record.placement || !skinData || skinData.length === 0) {
              throw new AppError(
                'COMPANION_UNSKINNED',
                `"${record.name}" has no skin weights yet — open it and generate skin weights ` +
                  'before using it as a preview model.',
                { projectId },
              );
            }
            // Throws AppError on a skinData/mesh mismatch; the guard surfaces it as-is.
            const assembled = assembleSkinnedRig(model.meshes, record.placement, skinData);
            root = assembled.root;
            rig = assembled.rig;
          }

          const companion: SessionCompanion = { projectId, root, rig };

          // The awaits above are long enough for the world to move: the user can close the
          // project, open another one, or hit the limit from a queued call. Re-check, and throw
          // away what we just loaded rather than attaching it to a scene it no longer belongs to.
          const stillOpen = store.getState().project?.id ?? null;
          if (
            stillOpen !== openId ||
            projectId === stillOpen ||
            session.companions.some((c) => c.projectId === projectId) ||
            session.companions.length >= MAX_COMPANIONS
          ) {
            disposeCompanion(companion);
            return;
          }

          ownerProjectId = stillOpen;
          meta.set(projectId, { projectId, name: record.name, kind: record.kind });
          owned.set(projectId, companion);
          session.addCompanion(companion);
          publish();
        } finally {
          busy = false;
        }
      });
    },

    removeCompanion(projectId: string): void {
      const companion = owned.get(projectId);
      if (!companion) return;
      session.removeCompanion(projectId);
      owned.delete(projectId);
      meta.delete(projectId);
      disposeCompanion(companion);
      publish();
    },

    clearCompanions(): void {
      if (owned.size === 0) return;
      dropAll();
      publish();
    },

    dispose(): void {
      unsubscribe();
      dropAll();
      // No `publish()` here: dispose runs while the whole editor is going away, and dispatching
      // an action into a store nobody listens to would only re-enter the notify loop.
    },
  };

  /**
   * Companions belong to the project they were loaded beside. `'model-changed'` is the one event
   * every project switch and close goes through (`loadIntoSession` / `closeProject`), and it is
   * emitted AFTER `setProject`, so the id read here is already the new one. Controllers are
   * created before the viewport mounts, so this listener runs first and the viewport's own
   * `'model-changed'` handler already sees an empty companion list.
   */
  const unsubscribe = events.on('model-changed', () => {
    const openId = store.getState().project?.id ?? null;
    if (openId === ownerProjectId) return;
    ownerProjectId = openId;
    controller.clearCompanions();
  });

  return controller;
}
